import React from 'react';
import { Box, Button, Flex, IconButton, Modal, Text, Tooltip } from 'pounceV2';
import { useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import { PANTHER_AI_NAME } from 'Source/constants';
import { AISummaryStreamParams } from 'Components/AIExplainLogEvents/useFetchGenericAiSummaryV3';
import useUrlParams from 'Hooks/useUrlParams';
import { useGetAIInferenceStreamMetadata } from 'Components/AIExplainLogEvents/graphql/getAiInferenceStreamMetadata.generated';
import useHover from 'Hooks/useHover';
import { AISummarizeKind } from 'Generated/schema';
import { createNewStreamId, isNewStreamId } from 'Pages/AI/constants';
import { useAINavigation } from 'Pages/AI/hooks/useAINavigation';
import {
  getHistoryTitleForKind,
  getActionLabelForKind,
} from 'Components/AIExplainLogEvents/utils/aiKindUtils';
import { ListAIInferenceStreamsMetadata } from 'Components/AIExplainLogEvents/graphql/listAiInferenceStreamsMetadata.generated';
import HistoryToggleButtonV3 from 'Components/buttons/HistoryToggleButton/HistoryToggleButtonV3';
import { useAIPageActionsV3 } from 'Pages/AI/hooks/useAIPageActionsV3';
import { printAIResponse } from 'Components/utils/printAIResponse';
import EditableThreadNameV3, { getStreamName } from './EditableThreadNameV3';
import ResponseNavigationControlV3 from './ResponseNavigationControlV3';

interface AISummaryHeaderV3Props {
  kind: AISummarizeKind;
  alias: string;
  onClose: () => void;
  onTitleClick: () => void;
  isThreadListOpen: boolean;
  loading: boolean;
  onDelete?: (id: string) => void;
  streamsMetadata?:
    | Array<ListAIInferenceStreamsMetadata['aiInferenceStreamsMetadata']['edges'][0]['node']>
    | undefined;
  isPrivate: boolean;
  onTogglePrivacy: () => void;
  canEdit: boolean;
  /** Whether the AI is currently loading/streaming */
  isLoading: boolean;
  /** The initial user prompt for print functionality */
  initialUserPrompt: string;
}

const AISummaryHeaderV3: React.FC<AISummaryHeaderV3Props> = ({
  kind,
  alias,
  onClose,
  onTitleClick,
  isThreadListOpen,
  loading,
  onDelete,
  streamsMetadata,
  isPrivate,
  onTogglePrivacy,
  canEdit,
  isLoading,
  initialUserPrompt,
}) => {
  const {
    urlParams: { aiStream, parentStreamId },
    updateUrlParams,
  } = useUrlParams<AISummaryStreamParams>();
  const {
    palette: { tokens },
  } = useTheme();

  const { handlers, isHovering } = useHover();
  const [isEditingName, setIsEditingName] = React.useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  // When creating a new stream, we should show the parent stream's data in the header
  const streamIdForMetadata = isNewStreamId(aiStream) ? parentStreamId : aiStream;
  const { data } = useGetAIInferenceStreamMetadata({
    variables: {
      id: streamIdForMetadata || '',
    },
    skip: !streamIdForMetadata || loading,
  });

  // Notify parent after a successful delete (not optimistically before)
  const handleResetAfterDelete = React.useCallback(() => {
    if (onDelete && streamIdForMetadata) {
      onDelete(streamIdForMetadata);
    }
  }, [onDelete, streamIdForMetadata]);

  const {
    handlePin,
    handleCopyLink,
    handleDelete,
    isLoading: editAIInferenceStreamLoading,
  } = useAIPageActionsV3({
    streamIdForMetadata,
    streamMetadata: data,
    onResetUI: handleResetAfterDelete,
    onClose,
    streamsMetadata,
  });

  const handleDeleteClick = React.useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const handleConfirmDelete = React.useCallback(() => {
    setShowDeleteConfirm(false);
    handleDelete();
  }, [handleDelete]);

  // Navigation control logic
  const navigationState = useAINavigation(aiStream, streamsMetadata);

  const handleNavigatePrevious = React.useCallback(() => {
    if (navigationState.previousStreamId) {
      updateUrlParams({ aiStream: navigationState.previousStreamId });
    }
  }, [navigationState.previousStreamId, updateUrlParams]);

  const handleNavigateNext = React.useCallback(() => {
    if (navigationState.nextStreamId) {
      updateUrlParams({ aiStream: navigationState.nextStreamId });
    }
  }, [navigationState.nextStreamId, updateUrlParams]);

  const title = React.useMemo(() => {
    if (isThreadListOpen) {
      return getHistoryTitleForKind(kind);
    }
    if (data?.aiInferenceStream) {
      return getStreamName(data.aiInferenceStream, kind);
    }
    return PANTHER_AI_NAME;
  }, [isThreadListOpen, kind, data?.aiInferenceStream]);

  const handlePrint = React.useCallback(() => {
    printAIResponse({ title, userPrompt: initialUserPrompt });
  }, [title, initialUserPrompt]);

  return (
    <Box
      display="flex"
      component="header"
      position="sticky"
      top={0}
      zIndex={2}
      alignItems="center"
      pt="16px"
      pb="10px"
      px={6}
      borderBottom={`1px solid ${tokens.borderPrimary}`}
      sx={{
        backgroundColor: tokens.background.brand.dark.base,
      }}
      {...handlers}
    >
      {isEditingName && data?.aiInferenceStream ? (
        <Box>
          <EditableThreadNameV3
            stream={data.aiInferenceStream}
            kind={kind}
            onCancelEdit={() => setIsEditingName(false)}
          />
        </Box>
      ) : (
        <Flex align="center" minHeight="38px" flex={1} minWidth={0}>
          <Flex align="center" spacing={2} minWidth={0} maxWidth="70%">
            <HistoryToggleButtonV3
              isOpen={isThreadListOpen}
              onClick={onTitleClick}
              // To avoid double-focus with the AbstractButton
              tabIndex={-1}
              disabled={isLoading}
            />
            {(data?.aiInferenceStream || isThreadListOpen) && (
              <Box
                onClick={onTitleClick}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { color: tokens.content.assets.accent[1].base },
                  flex: 1,
                  minWidth: 0,
                }}
                role="button"
                aria-label="Select AI response"
              >
                <Typography
                  fontSize="1.25rem"
                  fontWeight={400}
                  lineHeight={1.5}
                  textOverflow="ellipsis"
                  overflow="hidden"
                  whiteSpace="nowrap"
                  width="100%"
                >
                  {title}
                </Typography>
              </Box>
            )}
          </Flex>
          {!isLoading && !isThreadListOpen && !isEditingName && data?.aiInferenceStream && (
            <Box ml={2}>
              {isHovering && (
                <Tooltip content="Rename response" placement="top">
                  <IconButton
                    icon="pencil"
                    aria-label="Edit name"
                    variant="ghost"
                    size="medium"
                    onClick={() => setIsEditingName(true)}
                  />
                </Tooltip>
              )}
            </Box>
          )}
        </Flex>
      )}
      <Flex ml="auto" align="center" spacing={2} flexShrink={0}>
        {!isThreadListOpen && (
          <ResponseNavigationControlV3
            onNavigatePrevious={handleNavigatePrevious}
            onNavigateNext={handleNavigateNext}
            showPrevious={navigationState.showPrevious}
            showNext={navigationState.showNext}
          />
        )}
        {!isThreadListOpen && (
          <Tooltip content={`Start new ${getActionLabelForKind(kind)}`} placement="top">
            <IconButton
              icon="plus"
              aria-label={`Start new ${getActionLabelForKind(kind)}`}
              variant="ghost"
              size="medium"
              onClick={() => {
                // Plus button now always creates a new stream without parent relationship
                updateUrlParams({
                  aiStream: createNewStreamId(alias),
                });
              }}
            />
          </Tooltip>
        )}
        {!isThreadListOpen && data?.aiInferenceStream && canEdit && (
          <>
            <Tooltip content="Print response" placement="top">
              <Box as="span">
                <IconButton
                  icon="print"
                  aria-label="Print response"
                  variant="ghost"
                  size="medium"
                  disabled={isLoading}
                  onClick={handlePrint}
                />
              </Box>
            </Tooltip>
            <Tooltip
              content={data?.aiInferenceStream?.pinned ? 'Unpin' : 'Pin as default'}
              placement="top"
            >
              <Box as="span">
                <IconButton
                  icon={data?.aiInferenceStream?.pinned ? 'pin-filled' : 'pin-outline'}
                  aria-label={data?.aiInferenceStream?.pinned ? 'Unpin response' : 'Pin response'}
                  variant="ghost"
                  size="medium"
                  disabled={editAIInferenceStreamLoading}
                  onClick={handlePin}
                  style={{
                    color: data?.aiInferenceStream?.pinned
                      ? tokens.content.assets.accent[1].base
                      : 'inherit',
                  }}
                />
              </Box>
            </Tooltip>
            <Tooltip content="Copy URL" placement="top">
              <Box as="span">
                <IconButton
                  icon="copy-link"
                  aria-label="Copy URL"
                  variant="ghost"
                  size="medium"
                  onClick={handleCopyLink}
                />
              </Box>
            </Tooltip>
            <Tooltip
              content={
                isPrivate
                  ? 'This chat is private. Click to make it shared and visible to your organization'
                  : 'This chat is shared. Click to make it private and only visible to you'
              }
            >
              <Box as="span">
                <IconButton
                  icon={isPrivate ? 'lock' : 'shared'}
                  aria-label={isPrivate ? 'Make chat shared' : 'Make chat private'}
                  variant="ghost"
                  size="medium"
                  borderStyle="circle"
                  disabled={editAIInferenceStreamLoading}
                  onClick={onTogglePrivacy}
                />
              </Box>
            </Tooltip>
            <Tooltip content="Permanently delete this response" placement="top">
              <Box as="span">
                <IconButton
                  icon="trash"
                  aria-label="Delete response"
                  variant="ghost"
                  size="medium"
                  disabled={editAIInferenceStreamLoading}
                  onClick={handleDeleteClick}
                />
              </Box>
            </Tooltip>
          </>
        )}
        <IconButton
          icon="close"
          aria-label="Dismiss Side Panel"
          variant="outline"
          onClick={onClose}
          size="small"
        />
      </Flex>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        aria-labelledby="delete-confirmation-title"
        title="Confirm Delete"
        showCloseButton
      >
        <Box maxWidth={400}>
          <Text mb={6}>
            Are you sure you want to permanently delete this response? This action cannot be undone.
          </Text>
          <Flex justify="flex-end" gap={2}>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>
              Delete
            </Button>
          </Flex>
        </Box>
      </Modal>
    </Box>
  );
};

export default React.memo(AISummaryHeaderV3);
